import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Button, Card, CardContent, Dialog, DialogContent, DialogFooter, DialogHeader,
  DialogTitle, Input, Label, PageHeader, Badge,
} from '@erp/ui';
import { Plus, Truck, ChevronRight, Trash2 } from '@/components/icons';
import { SUPPORTED_DELIVERY_PROVIDER_CODES } from '@erp/delivery';
import {
  useDeliveryProviders,
  useCreateDeliveryProvider,
  useDeleteDeliveryProvider,
  useSetDefaultDeliveryProvider,
} from '@/hooks/use-delivery-providers';

const PROVIDER_LABELS: Record<string, string> = {
  intigo: 'Intigo',
  mock: 'Mock (dev)',
};

export function DeliveryProvidersListPage() {
  const { t } = useTranslation();
  const { data: providers, isLoading, error } = useDeliveryProviders();
  const setDefault = useSetDefaultDeliveryProvider();
  const remove = useDeleteDeliveryProvider();

  const [createOpen, setCreateOpen] = useState(false);

  const existingCodes = new Set((providers ?? []).map(p => p.code));
  const availableCodes = SUPPORTED_DELIVERY_PROVIDER_CODES.filter(c => !existingCodes.has(c));

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('settings.deliveryProviders.title', 'Transporteurs')}
        subtitle={t(
          'settings.deliveryProviders.subtitle',
          'Activez et configurez les API de livraison utilisées pour expédier les commandes',
        )}
      />

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h2 className="text-sm font-medium">
                {t('settings.deliveryProviders.listTitle', 'Transporteurs configurés')}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t(
                  'settings.deliveryProviders.listHint',
                  'Le transporteur par défaut est utilisé pour les nouvelles commandes',
                )}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => setCreateOpen(true)}
              disabled={availableCodes.length === 0}
            >
              <Plus size={14} />
              {t('common.add', 'Ajouter')}
            </Button>
          </div>

          {isLoading ? (
            <div className="text-sm text-muted-foreground">{t('common.loading', 'Chargement…')}</div>
          ) : error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
              {t(
                'settings.deliveryProviders.tableMissing',
                "Les tables transporteurs n'existent pas encore. Lancez la migration Supabase (npx supabase db push) puis rechargez la page.",
              )}
            </div>
          ) : !providers || providers.length === 0 ? (
            <div className="rounded-md border border-dashed p-6 text-center">
              <Truck size={20} className="mx-auto text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                {t(
                  'settings.deliveryProviders.empty',
                  'Aucun transporteur configuré. Ajoutez-en un pour commencer.',
                )}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border rounded-md border">
              {providers.map(p => (
                <li key={p.id} className="flex items-center gap-3 p-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Truck size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{p.displayName}</span>
                      {p.isDefault && (
                        <Badge variant="secondary" className="text-[10px]">
                          {t('settings.deliveryProviders.defaultBadge', 'Par défaut')}
                        </Badge>
                      )}
                      {p.isEnabled ? (
                        <Badge variant="default" className="text-[10px]">
                          {t('common.enabled', 'Activé')}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">
                          {t('common.disabled', 'Désactivé')}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{p.code}</div>
                  </div>

                  {p.isEnabled && !p.isDefault && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDefault.mutate(p.id)}
                      disabled={setDefault.isPending}
                    >
                      {t('settings.deliveryProviders.setDefault', 'Mettre par défaut')}
                    </Button>
                  )}

                  <Button asChild size="sm" variant="outline">
                    <Link to={`/settings/delivery-providers/${p.code}`}>
                      {t('common.configure', 'Configurer')}
                      <ChevronRight size={14} className="rtl:-scale-x-100" />
                    </Link>
                  </Button>

                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      if (confirm(t('common.confirmDelete', 'Supprimer ?') as string)) {
                        remove.mutate(p.id);
                      }
                    }}
                    disabled={remove.isPending}
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <CreateProviderDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        availableCodes={availableCodes as readonly string[]}
        isFirstProvider={(providers ?? []).length === 0}
      />
    </div>
  );
}

function CreateProviderDialog({
  open, onClose, availableCodes, isFirstProvider,
}: { open: boolean; onClose: () => void; availableCodes: readonly string[]; isFirstProvider: boolean }) {
  const { t } = useTranslation();
  const create = useCreateDeliveryProvider();
  const [code, setCode] = useState<string>('');
  const [displayName, setDisplayName] = useState('');

  function handleSelectCode(c: string) {
    setCode(c);
    setDisplayName(PROVIDER_LABELS[c] ?? c);
  }

  function handleSubmit() {
    if (!code || !displayName.trim()) return;
    // First provider added is always activated and made default — saves the
    // user from a redundant click. They can always toggle off later.
    create.mutate(
      {
        code,
        displayName: displayName.trim(),
        isEnabled: isFirstProvider,
        isDefault: isFirstProvider,
      },
      {
        onSuccess: () => {
          setCode('');
          setDisplayName('');
          onClose();
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('settings.deliveryProviders.create.title', 'Ajouter un transporteur')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t('settings.deliveryProviders.create.codeLabel', 'Transporteur')}</Label>
            <div className="flex flex-wrap gap-2">
              {availableCodes.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => handleSelectCode(c)}
                  className={`px-3 py-1.5 rounded-md border text-sm ${
                    code === c
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  {PROVIDER_LABELS[c] ?? c}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="dp-name">
              {t('settings.deliveryProviders.create.nameLabel', "Nom d'affichage")}
            </Label>
            <Input
              id="dp-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Intigo"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button onClick={handleSubmit} disabled={!code || !displayName.trim() || create.isPending}>
            {t('common.save', 'Enregistrer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

